// Page des messages système pour l'utilisateur
import React, { useState } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import MessageCard from '../components/MessageCard';
import MessageDetailModal from '../components/MessageDetailModal';
import { useMessage } from '../contexts/MessageContext';
import { toast } from 'sonner';
import { Mail, CheckCheck, Inbox } from 'lucide-react';
import { Button } from '../components/ui/button';

// Filtres disponibles
const FILTERS = [
  { key: 'all', label: 'Tous' },
  { key: 'unread', label: 'Non lus' },
  { key: 'feature', label: '🆕 Nouveautés' },
  { key: 'news', label: '📢 Actualités' },
  { key: 'promo', label: '💎 Promotions' },
  { key: 'maintenance', label: '🔧 Maintenance' },
  { key: 'update', label: '⚠️ Importants' },
];

const MessagesPage = () => {
  const { messages, unreadCount, loading, markAllAsRead } = useMessage();
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [activeFilter, setActiveFilter] = useState('all');

  // Ouvrir le modal de détail
  const handleMessageClick = (message) => {
    setSelectedMessage(message);
    setModalOpen(true);
  };

  // Fermer le modal
  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedMessage(null);
  };

  // Marquer tout comme lu
  const handleMarkAllRead = async () => {
    try {
      await markAllAsRead();
      toast.success('Tous les messages marqués comme lus');
    } catch {
      toast.error('Erreur lors du marquage');
    }
  };

  // Filtrer les messages selon le filtre actif
  const filteredMessages = messages.filter((msg) => {
    if (activeFilter === 'all') return true;
    if (activeFilter === 'unread') return !msg.isRead;
    return msg.type === activeFilter;
  });

  return (
    <DashboardLayout>
      <div className="max-w-3xl mx-auto">
        {/* En-tête de page */}
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-xl">
              <Mail className="w-6 h-6 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Messages</h1>
              {unreadCount > 0 && (
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {unreadCount} message{unreadCount > 1 ? 's' : ''} non lu{unreadCount > 1 ? 's' : ''}
                </p>
              )}
            </div>
          </div>

          {unreadCount > 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleMarkAllRead}
              className="flex items-center gap-2 text-sm"
            >
              <CheckCheck className="w-4 h-4" />
              Tout marquer comme lu
            </Button>
          )}
        </div>

        {/* Barre de filtres */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2 scrollbar-hide">
          {FILTERS.map((filter) => (
            <button
              key={filter.key}
              onClick={() => setActiveFilter(filter.key)}
              className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                activeFilter === filter.key
                  ? 'bg-blue-600 text-white shadow-sm'
                  : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
              }`}
            >
              {filter.label}
              {filter.key === 'unread' && unreadCount > 0 && (
                <span className="ml-1 bg-white/30 text-current px-1 rounded-full">
                  {unreadCount}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* État de chargement */}
        {loading && (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className="h-28 rounded-xl bg-gray-100 dark:bg-gray-800 animate-pulse"
              />
            ))}
          </div>
        )}

        {/* Liste des messages */}
        {!loading && filteredMessages.length > 0 && (
          <div className="space-y-3">
            {filteredMessages.map((message) => (
              <MessageCard
                key={message.id}
                message={message}
                onClick={handleMessageClick}
              />
            ))}
          </div>
        )}

        {/* État vide */}
        {!loading && filteredMessages.length === 0 && (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <div className="p-4 bg-gray-100 dark:bg-gray-800 rounded-full mb-4">
              <Inbox className="w-10 h-10 text-gray-400 dark:text-gray-500" />
            </div>
            <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-300 mb-1">
              {activeFilter === 'unread' ? 'Aucun message non lu' : 'Aucun message'}
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {activeFilter === 'unread'
                ? 'Vous avez tout lu !'
                : 'Les messages de l\'équipe Remadia apparaîtront ici.'}
            </p>
            {activeFilter !== 'all' && (
              <button
                onClick={() => setActiveFilter('all')}
                className="mt-3 text-sm text-blue-600 dark:text-blue-400 hover:underline"
              >
                Voir tous les messages
              </button>
            )}
          </div>
        )}
      </div>

      {/* Modal détail message */}
      <MessageDetailModal
        message={selectedMessage}
        open={modalOpen}
        onClose={handleCloseModal}
      />
    </DashboardLayout>
  );
};

export default MessagesPage;
